import { TextButton } from '@creditornot/cb-button';
import { FormRow } from '@creditornot/cb-form';
import { typographyCss, cssThemedValue } from '@creditornot/cb-ingredients';
import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { localize } from 'common/utils';

import { OptionChoiceAdvancedDetailsViewModal } from './OptionChoiceAdvancedDetailsViewModal';
import { OptionChoiceView } from './OptionChoiceView';
import { useSelectionData } from '../../../../hooks/useSelectionData';
import { Option } from '../../types';
import { Root, StyledTabs, TabbedSection, TabsContainer } from '../CommonPanelStyles';
import { OfferLinks } from '../OptionForm/OfferLinks';
import { OptionTab, optionTabs } from '../OptionForm/constants';
import { TabCountIcon } from '../TabCountIcon';

type Props = {
  language: Language;
  option: Option;
};

const DetailsContainer = styled.div`
  padding: 0 24px 80px;
`;

const Section = styled.div`
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
`;

const Heading = styled.div`
  ${typographyCss.Title1};
  display: flex;
  align-items: center;
  padding: 24px 0;
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
`;

const AdvancedDetailsModalToggleContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const optionTypeToTranslationStringId: { [key in Option['type']]: string } = {
  bool: 'menu-option.switch',
  choice: 'menu-option.single-choice',
  multi_choice: 'menu-option.multi-choice',
};

const optionAmountToTranslationStringId: { [key in Option['type']]: string } = {
  bool: 'menu-option.choices-one',
  choice: 'menu-option.choices-one',
  multi_choice: 'menu-option.choices-many',
};

export const OptionView = ({ language, option }: Props) => {
  const intl = useIntl();
  const { selection, offers } = useSelectionData();
  const [activeTabId, setActiveTabId] = useState<OptionTab>(optionTabs.DETAILS);

  const [isAdvancedDetailsModalOpen, setIsAdvancedDetailsModalOpen] = useState(false);

  const offersUsingCurrentOption = useMemo(
    () => (option ? offers.filter(offer => !!offer.option_bindings?.find(ob => ob.option_id === option.id)) : []),
    [option, offers],
  );

  const isChoice = useMemo(() => ['choice', 'multi_choice'].includes(option.type), [option.type]);

  return (
    <>
      <TabsContainer>
        <StyledTabs
          activeTabId={activeTabId}
          items={[
            {
              id: optionTabs.DETAILS,
              label: intl.formatMessage({ id: 'shared-menu.editor.option-tabs.details' }),
            },
            {
              id: optionTabs.ITEMS,
              label: intl.formatMessage({ id: 'shared-menu.editor.option-tabs.items' }),
              rightContent: (
                <TabCountIcon
                  count={offersUsingCurrentOption.length}
                  variant={activeTabId === optionTabs.ITEMS ? 'active' : 'info'}
                />
              ),
            },
          ]}
          onChange={(id: OptionTab) => setActiveTabId(id)}
        />
      </TabsContainer>
      <Root>
        <TabbedSection $isShown={activeTabId === optionTabs.DETAILS}>
          <DetailsContainer>
            <Section>
              <Heading>
                <FormattedMessage id="menu-option.details-header" />
              </Heading>
              <FormRow label={intl.formatMessage({ id: 'menu-option.option-type-label' })}>
                <FormattedMessage id={optionTypeToTranslationStringId[option.type]} />
              </FormRow>
              <FormRow
                label={intl.formatMessage({
                  id: isChoice ? 'menu-option.choice-title-label' : 'menu-option.bool-title-label',
                })}
              >
                {localize(option.name, language)}
              </FormRow>
              {isChoice && (
                <FormRow label={intl.formatMessage({ id: 'menu-option.choices-amount-label' })}>
                  <FormattedMessage id={optionAmountToTranslationStringId[option.type]} />
                </FormRow>
              )}
              <FormRow label={intl.formatMessage({ id: 'menu-option.show-to-courier' })}>
                {option.values.some(value => value.visible_to_courier) ? (
                  <FormattedMessage id="boolean.true" />
                ) : (
                  <FormattedMessage id="boolean.false" />
                )}
              </FormRow>
              <FormRow label={intl.formatMessage({ id: 'menu.pos-id' })}>{option.external_id || '-'}</FormRow>
            </Section>
            <div>
              <Heading>
                <FormattedMessage id="menu-option.choices-header" />
              </Heading>
              {option.values.map((choice, index) => (
                <OptionChoiceView
                  language={language}
                  optionId={option.id}
                  optionChoice={choice}
                  isDefault={option.default_value_index === index}
                  isChoice={isChoice}
                  isMultiChoice={option.type === 'multi_choice'}
                  key={choice.id}
                />
              ))}
            </div>
            <AdvancedDetailsModalToggleContainer>
              <TextButton onClick={() => setIsAdvancedDetailsModalOpen(true)} variant="blue">
                <FormattedMessage id="menu-option.advanced-details" />
              </TextButton>
            </AdvancedDetailsModalToggleContainer>
            <OptionChoiceAdvancedDetailsViewModal
              isOpen={isAdvancedDetailsModalOpen}
              onClose={() => setIsAdvancedDetailsModalOpen(false)}
              optionChoices={option.values}
              primaryLanguage={selection.primary_language}
            />
          </DetailsContainer>
        </TabbedSection>

        <TabbedSection $isShown={activeTabId === optionTabs.ITEMS}>
          <OfferLinks activeOption={option} offersUsingCurrentOption={offersUsingCurrentOption} />
        </TabbedSection>
      </Root>
    </>
  );
};
